// apps/provider/src/stores/calendarAlertStore.js
// Route-persistent, id-keyed caches for the calendar's alert layer:
// billing-rule context (per case), prior-auth violations (per patient),
// POC-expiration violations (per case), and org payer alerts.
//
// Delta SWR model: each ensure* call fetches ONLY ids that are missing or
// older than ALERT_FRESH_MS, merging results without ever blanking
// existing entries. fetchedAt is stamped for EVERY requested id — including
// ids with empty results — so patients/cases with no auths/POCs don't cause
// refetch loops.
//
// Store actions are mechanism-only (plain id arrays in, plain maps out) — no
// calendar coupling. Policy (e.g. the cash-mode prior-auth guard) lives in
// the consuming hook. Billing context is cached RAW (evaluation needs live
// appointment data and is cheap to re-run in a memo); prior-auth/POC
// violations are cached pre-derived (pure per-entity).

import { create } from 'zustand';
import { apiService } from '../services/api';
import { getPriorAuthAlert, getPocExpirationViolation, isPocExpired } from '../utils/billingAlertEnrichment';
import { isMedicareCoverage } from '../utils/pnDue';

// Alert entries refresh on EVENTS (invalidateForAppointment via realtime); this
// TTL is only the correctness backstop for changes with no event coverage
// (note signings, POC recerts, auth edits, rule-config changes)
const ALERT_FRESH_MS = 30 * 60 * 1000;
// Trailing-edge coalescing for event-driven invalidation: an invalidated id
// becomes refetchable this long after its LAST invalidation, so repeated
// writes to one record collapse into a single alert refetch cycle instead of
// one per write per tab (write-amplification observed at peak: ~500 req/min)
const INVALIDATION_COOLDOWN_MS = 30000;
// Random spread on top of the cooldown: every tab invalidates the same entry
// at the same instant (same realtime event), so a fixed cooldown made them all
// refetch alerts in the same second (2026-09-22: 20-33/s per batch endpoint).
const INVALIDATION_JITTER_MAX_MS = 15000;
const CONTEXT_CACHE_CAP = 2000; // billingContextByCase entries — must exceed a big org's month-view case count
const VIOLATION_CACHE_CAP = 5000; // priorAuthByPatient / pocByCase entries
const POC_CHUNK_SIZE = 50; // guards getPlansOfCare's limit:200 truncation
const CONTEXT_CHUNK_SIZE = 150; // bounds per-request pod memory on calendar-billing-context
const PRIOR_AUTH_CHUNK_SIZE = 100; // the batch endpoint's nested loops scale badly with patient count
const UPCOMING_CHUNK_SIZE = 100; // patient-upcoming-counts caps patient_ids at 100 per call
const FORM_ALERT_CHUNK_SIZE = 100; // patient-alerts-batch: one indexed `in` query per chunk
const VISIT_LIMIT_CHUNK_SIZE = 100; // patient-visit-limit-alerts-batch caps patient_ids at 100 per call

// ---------- module-level bookkeeping (not reactive state) ----------

// in-flight fetch dedupe: key -> Promise
const inFlight = new Map();

const initialState = {
  billingRules: [],
  rulesFetchedAt: null,
  // case_id -> { encounters, clinical_notes, plans_of_care, caseRecord, fetchedAt }
  billingContextByCase: {},
  // patient_id -> { violation: string|null, fetchedAt }
  priorAuthByPatient: {},
  // case_id -> { violation: string|null, fetchedAt }
  pocByCase: {},
  // patient_id -> { count: number, fetchedAt }
  upcomingByPatient: {},
  // patient_id -> { alerts: PatientAlertBatchRow[], fetchedAt } (persisted form answer alerts)
  formAlertsByPatient: {},
  // patient_id -> { items: VisitLimitAlertRow[], fetchedAt } (low insurance benefit limits)
  visitLimitsByPatient: {},
  // claimmd_payer_id -> alert message
  payerAlertMap: {},
  payerAlertMapById: {},
  // Slimmed org payer list for legend payer-group resolution:
  // [{ id, claimmd_payer_id, display_name, payer_group_id }]
  payerRoster: [],
  // payer_group id -> name (legend labels)
  payerGroupsById: {},
  payerFetchedAt: null,
};

// Numeric-aware comparator: case ids are ints, patient ids are uuids. Sorting
// makes chunk composition deterministic across clients (stable request bodies
// → future endpoint caching can actually hit).
const compareIds = (a, b) => {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b));
};

// Ids that are missing from the cache or older than the freshness window,
// returned SORTED (deterministic chunks)
const idsNeedingFetch = (ids, cacheMap) => {
  const now = Date.now();
  const need = [];
  const seen = new Set();
  for (const id of ids) {
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    const entry = cacheMap[id];
    if (!entry || now - entry.fetchedAt >= ALERT_FRESH_MS) need.push(id);
  }
  return need.sort(compareIds);
};

const groupBy = (arr, key) => {
  const map = {};
  for (const item of arr || []) {
    const k = item?.[key];
    if (k == null) continue;
    if (!map[k]) map[k] = [];
    map[k].push(item);
  }
  return map;
};

// Drop oldest-fetchedAt entries when a cache exceeds its cap. protectedIds
// (the ids of the CURRENT request) are never evicted — evicting just-fetched
// entries made them "missing" on the next render and caused a refetch-thrash
// loop when a big org's visible id set exceeded the cap (prod incident 7/14).
const evictOldest = (map, cap, protectedIds) => {
  const keys = Object.keys(map);
  if (keys.length <= cap) return map;
  const evictable = protectedIds
    ? keys.filter(k => !protectedIds.has(k))
    : keys;
  const sorted = evictable.sort((a, b) => (map[a].fetchedAt || 0) - (map[b].fetchedAt || 0));
  const dropCount = Math.min(keys.length - cap, sorted.length);
  const next = { ...map };
  for (let i = 0; i < dropCount; i++) delete next[sorted[i]];
  return next;
};

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export const useCalendarAlertStore = create((set, get) => ({
  ...initialState,

  // ---------- billing rule context (raw, per case) ----------

  ensureBillingContext: async ({ caseIds = [], organizationId }) => {
    if (!organizationId) return;
    const need = idsNeedingFetch(caseIds, get().billingContextByCase);
    if (!need.length) return;

    const flightKey = `bc|${[...need].sort().join(',')}`;
    if (inFlight.has(flightKey)) return inFlight.get(flightKey);

    const fetchedAt = Date.now();
    const protectedIds = new Set(need.map(String));
    const run = (async () => {
      try {
        // Chunked: one giant request holding a whole month of context in pod
        // memory is what OOM'd the instance — bound each call's working set
        const results = await Promise.all(
          chunk(need, CONTEXT_CHUNK_SIZE).map((ids) =>
            apiService.getCalendarBillingContext({
              case_ids: ids.join(','),
              organization_id: organizationId,
              tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
          )
        );

        // v2 contract: per-case SQL aggregates + raw POC rows; a case with no
        // (non-cancelled) encounters produces no aggregate row → aggregate null
        const aggregatesByCase = {};
        for (const a of results.flatMap(r => r?.case_aggregates || [])) {
          if (a?.case_id != null) aggregatesByCase[a.case_id] = a;
        }
        const pocsByCase = groupBy(results.flatMap(r => r?.plans_of_care || []), 'case_id');
        const casesById = {};
        for (const c of results.flatMap(r => r?.cases || [])) {
          if (c?.id != null) casesById[c.id] = c;
        }
        const rules = results.map(r => r?.rules).find(r => Array.isArray(r));

        set((state) => {
          const next = { ...state.billingContextByCase };
          // Stamp EVERY requested id, even when the case has no context rows
          for (const caseId of need) {
            next[caseId] = {
              aggregate: aggregatesByCase[caseId] || null,
              plans_of_care: pocsByCase[caseId] || [],
              caseRecord: casesById[caseId] || null,
              fetchedAt,
            };
          }
          return {
            billingContextByCase: evictOldest(next, CONTEXT_CACHE_CAP, protectedIds),
            // Rules ride along on every billing-context response
            billingRules: rules || state.billingRules,
            rulesFetchedAt: fetchedAt,
          };
        });
      } catch (err) {
        console.error('calendarAlertStore: billing context fetch failed:', err);
        // Negative-cache the failure: stamp requested ids (preserving any
        // existing data) so retries wait out the freshness window instead of
        // hammering a struggling instance on every render
        set((state) => {
          const next = { ...state.billingContextByCase };
          for (const caseId of need) {
            next[caseId] = next[caseId]
              ? { ...next[caseId], fetchedAt }
              : { aggregate: null, plans_of_care: [], caseRecord: null, fetchedAt };
          }
          return { billingContextByCase: evictOldest(next, CONTEXT_CACHE_CAP, protectedIds) };
        });
      } finally {
        inFlight.delete(flightKey);
      }
    })();

    inFlight.set(flightKey, run);
    return run;
  },

  // ---------- prior authorization violations (derived, per patient) ----------

  ensurePriorAuth: async ({ patientIds = [] }) => {
    const need = idsNeedingFetch(patientIds, get().priorAuthByPatient);
    if (!need.length) return;

    const flightKey = `pa|${[...need].sort().join(',')}`;
    if (inFlight.has(flightKey)) return inFlight.get(flightKey);

    const fetchedAt = Date.now();
    const protectedIds = new Set(need.map(String));
    const stampAll = (state) => {
      const next = { ...state.priorAuthByPatient };
      for (const patientId of need) {
        next[patientId] = next[patientId]
          ? { ...next[patientId], fetchedAt }
          : { violation: null, fetchedAt };
      }
      return { priorAuthByPatient: evictOldest(next, VIOLATION_CACHE_CAP, protectedIds) };
    };
    const run = (async () => {
      try {
        const results = await Promise.all(
          chunk(need, PRIOR_AUTH_CHUNK_SIZE).map((ids) =>
            apiService.getPriorAuthorizationAlertsBatch(ids)
          )
        );
        const auths = results.flatMap((r) => (Array.isArray(r) ? r : []));
        const byPatient = groupBy(auths, 'patient_id');

        set((state) => {
          const next = { ...state.priorAuthByPatient };
          for (const patientId of need) {
            next[patientId] = {
              // Structured {message, used, authorized, ...} (was a plain string before 2026-09-21)
              violation: byPatient[patientId] ? getPriorAuthAlert(byPatient[patientId]) : null,
              fetchedAt,
            };
          }
          return { priorAuthByPatient: evictOldest(next, VIOLATION_CACHE_CAP, protectedIds) };
        });
      } catch (err) {
        console.error('calendarAlertStore: prior auth fetch failed:', err);
        // Negative-cache failures so retries wait out the freshness window
        set(stampAll);
      } finally {
        inFlight.delete(flightKey);
      }
    })();

    inFlight.set(flightKey, run);
    return run;
  },

  // ---------- upcoming appointment counts (per patient) ----------

  ensureUpcomingCounts: async ({ patientIds = [] }) => {
    const need = idsNeedingFetch(patientIds, get().upcomingByPatient);
    if (!need.length) return;

    const flightKey = `uc|${[...need].sort().join(',')}`;
    if (inFlight.has(flightKey)) return inFlight.get(flightKey);

    const fetchedAt = Date.now();
    const protectedIds = new Set(need.map(String));
    const stampAll = (state) => {
      const next = { ...state.upcomingByPatient };
      for (const patientId of need) {
        const key = String(patientId);
        next[key] = { count: next[key]?.count ?? 0, fetchedAt };
      }
      return { upcomingByPatient: evictOldest(next, VIOLATION_CACHE_CAP, protectedIds) };
    };
    const run = (async () => {
      try {
        const results = await Promise.all(
          chunk(need, UPCOMING_CHUNK_SIZE).map((ids) =>
            apiService.getPatientUpcomingCounts(ids)
          )
        );
        const countById = {};
        for (const r of results) {
          for (const item of (Array.isArray(r?.items) ? r.items : [])) {
            if (item?.patient_id != null) countById[String(item.patient_id)] = Number(item.upcoming_count) || 0;
          }
        }

        set((state) => {
          const next = { ...state.upcomingByPatient };
          // Stamp EVERY requested id: the endpoint omits zero-count patients
          for (const patientId of need) {
            const key = String(patientId);
            next[key] = { count: countById[key] ?? 0, fetchedAt };
          }
          return { upcomingByPatient: evictOldest(next, VIOLATION_CACHE_CAP, protectedIds) };
        });
      } catch (err) {
        console.error('calendarAlertStore: upcoming counts fetch failed:', err);
        // Negative-cache failures so retries wait out the freshness window
        set(stampAll);
      } finally {
        inFlight.delete(flightKey);
      }
    })();

    inFlight.set(flightKey, run);
    return run;
  },

  // ---------- low insurance benefit visit limits (per patient) ----------

  ensureVisitLimits: async ({ patientIds = [] }) => {
    const need = idsNeedingFetch(patientIds, get().visitLimitsByPatient);
    if (!need.length) return;

    const flightKey = `vl|${[...need].sort().join(',')}`;
    if (inFlight.has(flightKey)) return inFlight.get(flightKey);

    const fetchedAt = Date.now();
    const protectedIds = new Set(need.map(String));
    const stampAll = (state) => {
      const next = { ...state.visitLimitsByPatient };
      for (const patientId of need) {
        const key = String(patientId);
        next[key] = { items: next[key]?.items ?? [], fetchedAt };
      }
      return { visitLimitsByPatient: evictOldest(next, VIOLATION_CACHE_CAP, protectedIds) };
    };
    const run = (async () => {
      try {
        const results = await Promise.all(
          chunk(need, VISIT_LIMIT_CHUNK_SIZE).map((ids) =>
            apiService.getPatientVisitLimitAlertsBatch(ids)
          )
        );
        const itemsById = {};
        for (const r of results) {
          for (const item of (Array.isArray(r?.items) ? r.items : [])) {
            if (item?.patient_id == null) continue;
            const key = String(item.patient_id);
            if (!itemsById[key]) itemsById[key] = [];
            itemsById[key].push(item);
          }
        }

        set((state) => {
          const next = { ...state.visitLimitsByPatient };
          // Stamp EVERY requested id: the endpoint omits patients with nothing low
          for (const patientId of need) {
            const key = String(patientId);
            next[key] = { items: itemsById[key] ?? [], fetchedAt };
          }
          return { visitLimitsByPatient: evictOldest(next, VIOLATION_CACHE_CAP, protectedIds) };
        });
      } catch (err) {
        console.error('calendarAlertStore: visit limit alerts fetch failed:', err);
        // Negative-cache failures so retries wait out the freshness window
        set(stampAll);
      } finally {
        inFlight.delete(flightKey);
      }
    })();

    inFlight.set(flightKey, run);
    return run;
  },

  // ---------- POC expiration violations (derived, per case) ----------

  ensurePoc: async ({ caseIds = [] }) => {
    const need = idsNeedingFetch(caseIds, get().pocByCase);
    if (!need.length) return;

    const flightKey = `poc|${[...need].sort().join(',')}`;
    if (inFlight.has(flightKey)) return inFlight.get(flightKey);

    const fetchedAt = Date.now();
    const protectedIds = new Set(need.map(String));
    const stampAll = (state) => {
      const next = { ...state.pocByCase };
      for (const caseId of need) {
        next[caseId] = next[caseId]
          ? { ...next[caseId], fetchedAt }
          : { violation: null, fetchedAt };
      }
      return { pocByCase: evictOldest(next, VIOLATION_CACHE_CAP, protectedIds) };
    };
    const run = (async () => {
      try {
        // Chunk to stay well inside the endpoint's limit:200 page size
        const results = await Promise.all(
          chunk(need, POC_CHUNK_SIZE).map((ids) =>
            apiService.getPlansOfCare({ case_ids: ids.join(','), limit: 200 })
          )
        );
        const plans = results.flatMap((r) => r?.items || []);
        const byCase = groupBy(plans, 'case_id');

        set((state) => {
          const next = { ...state.pocByCase };
          for (const caseId of need) {
            const rows = byCase[caseId];
            next[caseId] = {
              violation: rows ? getPocExpirationViolation(rows) : null,
              expired: rows ? isPocExpired(rows) : false,
              // Case billing coverage is Medicare/MA (rows carry the full
              // `case` and `patient` addons). Setting-agnostic data: the
              // poc_alerts_medicare_only gate is applied in useCalendarAlerts.
              medicare: rows
                ? isMedicareCoverage(rows.find(p => p.case)?.case, rows.find(p => p.patient)?.patient)
                : false,
              fetchedAt,
            };
          }
          return { pocByCase: evictOldest(next, VIOLATION_CACHE_CAP, protectedIds) };
        });
      } catch (err) {
        console.error('calendarAlertStore: plan of care fetch failed:', err);
        // Negative-cache failures so retries wait out the freshness window
        set(stampAll);
      } finally {
        inFlight.delete(flightKey);
      }
    })();

    inFlight.set(flightKey, run);
    return run;
  },

  // ---------- org payer alerts ----------

  ensurePayers: async () => {
    const { payerFetchedAt } = get();
    // Payer alert config changes rarely — same 30-min freshness as the rest
    // (was 60s, which cost ~30 req/min org-wide at peak)
    if (payerFetchedAt && Date.now() - payerFetchedAt < ALERT_FRESH_MS) return;

    const flightKey = 'payers';
    if (inFlight.has(flightKey)) return inFlight.get(flightKey);

    const fetchedAt = Date.now();
    const run = (async () => {
      try {
        // Groups ride the same fetch cycle: one small indexed query alongside
        // the payer list, feeding legend payer-group resolution and labels
        const [payers, groups] = await Promise.all([
          apiService.getOrganizationPayers(),
          apiService.getPayerGroups().catch(() => []),
        ]);
        const alertMap = {};
        const alertMapById = {};
        const roster = [];
        (Array.isArray(payers) ? payers : []).forEach((p) => {
          if (p.id) {
            roster.push({
              id: p.id,
              claimmd_payer_id: p.claimmd_payer_id,
              display_name: p.display_name,
              payer_group_id: p.payer_group_id,
            });
          }
          if (!p.alert_appointments) return;
          if (p.claimmd_payer_id) alertMap[p.claimmd_payer_id] = p.alert_appointments;
          if (p.id) alertMapById[p.id] = p.alert_appointments;
        });
        const groupsById = {};
        (Array.isArray(groups) ? groups : []).forEach((g) => {
          if (g.id) groupsById[g.id] = g.name;
        });
        set({
          payerAlertMap: alertMap,
          payerAlertMapById: alertMapById,
          payerRoster: roster,
          payerGroupsById: groupsById,
          payerFetchedAt: fetchedAt,
        });
      } catch {
        // Non-critical, matches prior behavior
      } finally {
        inFlight.delete(flightKey);
      }
    })();

    inFlight.set(flightKey, run);
    return run;
  },

  // ---------- persisted patient alerts (form answer alerts, per patient) ----------

  ensureFormAlerts: async ({ patientIds = [] }) => {
    const need = idsNeedingFetch(patientIds, get().formAlertsByPatient);
    if (!need.length) return;

    const flightKey = `fa|${[...need].sort().join(',')}`;
    if (inFlight.has(flightKey)) return inFlight.get(flightKey);

    const fetchedAt = Date.now();
    const protectedIds = new Set(need.map(String));
    const stampAll = (state) => {
      const next = { ...state.formAlertsByPatient };
      for (const patientId of need) {
        next[patientId] = next[patientId]
          ? { ...next[patientId], fetchedAt }
          : { alerts: [], fetchedAt };
      }
      return { formAlertsByPatient: evictOldest(next, VIOLATION_CACHE_CAP, protectedIds) };
    };
    const run = (async () => {
      try {
        const results = await Promise.all(
          chunk(need, FORM_ALERT_CHUNK_SIZE).map((ids) =>
            apiService.getPatientAlertsBatch(ids)
          )
        );
        const rows = results.flatMap((r) => (Array.isArray(r) ? r : []));
        const byPatient = groupBy(rows, 'patient_id');

        set((state) => {
          const next = { ...state.formAlertsByPatient };
          for (const patientId of need) {
            next[patientId] = { alerts: byPatient[patientId] || [], fetchedAt };
          }
          return { formAlertsByPatient: evictOldest(next, VIOLATION_CACHE_CAP, protectedIds) };
        });
      } catch (err) {
        console.error('calendarAlertStore: form alerts fetch failed:', err);
        // Negative-cache failures so retries wait out the freshness window
        set(stampAll);
      } finally {
        inFlight.delete(flightKey);
      }
    })();

    inFlight.set(flightKey, run);
    return run;
  },

  // ---------- event-driven invalidation ----------

  // Called when an appointment changes (status change, realtime create/update
  // ingest): stamp the alert entries for its case/patient so they become
  // refetchable INVALIDATION_COOLDOWN_MS after the LAST invalidation
  // (trailing-edge coalescing — repeated writes push the refetch out, so N
  // rapid writes cost one refetch cycle). Re-stamp rather than delete — the
  // cached alert keeps rendering until fresh data lands (no flicker). Status
  // itself patches instantly in calendarStore; only the advisory badge lags.
  invalidateForAppointment: ({ caseId, patientId } = {}) => {
    // Entry becomes stale (idsNeedingFetch: now - fetchedAt >= ALERT_FRESH_MS)
    // exactly INVALIDATION_COOLDOWN_MS from now
    const staleAt = Date.now() - ALERT_FRESH_MS + INVALIDATION_COOLDOWN_MS + Math.random() * INVALIDATION_JITTER_MAX_MS;
    set((state) => {
      const next = {};
      if (caseId != null && state.billingContextByCase[caseId]) {
        next.billingContextByCase = {
          ...state.billingContextByCase,
          [caseId]: { ...state.billingContextByCase[caseId], fetchedAt: staleAt },
        };
      }
      if (caseId != null && state.pocByCase[caseId]) {
        next.pocByCase = {
          ...state.pocByCase,
          [caseId]: { ...state.pocByCase[caseId], fetchedAt: staleAt },
        };
      }
      if (patientId != null && state.visitLimitsByPatient[patientId]) {
        next.visitLimitsByPatient = {
          ...state.visitLimitsByPatient,
          [patientId]: { ...state.visitLimitsByPatient[patientId], fetchedAt: staleAt },
        };
      }
      if (patientId != null && state.priorAuthByPatient[patientId]) {
        next.priorAuthByPatient = {
          ...state.priorAuthByPatient,
          [patientId]: { ...state.priorAuthByPatient[patientId], fetchedAt: staleAt },
        };
      }
      if (patientId != null && state.upcomingByPatient[patientId]) {
        next.upcomingByPatient = {
          ...state.upcomingByPatient,
          [patientId]: { ...state.upcomingByPatient[patientId], fetchedAt: staleAt },
        };
      }
      if (patientId != null && state.formAlertsByPatient[patientId]) {
        next.formAlertsByPatient = {
          ...state.formAlertsByPatient,
          [patientId]: { ...state.formAlertsByPatient[patientId], fetchedAt: staleAt },
        };
      }
      return next;
    });
  },

  // ---------- lifecycle ----------

  reset: () => {
    set({ ...initialState });
  },
}));
